import { statusStore, StatusWorkflow } from '@/lib/stores/statusStore';

/**
 * Get the color for a specific status within a workflow type
 * @param status - The status value (e.g., "draft", "review", "approved")
 * @param workflowType - The type of workflow (objects, templates, documents, dialogs, loca)
 * @param gameId - The game ID to look up status configuration
 * @returns The hex color string or a default color
 */
export function getStatusColor(
  status: string | undefined,
  workflowType: 'objects' | 'templates' | 'documents' | 'dialogs' | 'loca',
  gameId?: string
): string {
  if (!status) {
    return '#000000'; // Default black if no status
  }

  // Get status configurations from store
  const statusConfigs = statusStore.getData();
  
  if (!statusConfigs || statusConfigs.length === 0) {
    return getDefaultStatusColor(status);
  }

  // Find the relevant status config
  // First try game-specific, then company-wide
  const gameConfig = gameId 
    ? statusConfigs.find(config => config.game_id === gameId)
    : null;
  
  const companyConfig = statusConfigs.find(config => config.game_id === null);
  
  const config = gameConfig || companyConfig;
  
  if (!config) {
    return getDefaultStatusColor(status);
  }

  // Get the workflow for this type
  const workflow: StatusWorkflow = config[workflowType] || {};
  
  // Get the color for this status
  const statusColor = workflow[status]?.color;
  
  return statusColor || getDefaultStatusColor(status);
}

/**
 * Get default status colors (fallback when no configuration exists)
 */
export function getDefaultStatusColor(status: string): string {
  const defaultColors: Record<string, string> = {
    'draft': '#d3d933',
    'review': '#354dff',
    'approved': '#a9d18e',
    'rejected': '#ffc000',
    'published': '#6e35ff',
    'implemented': '#8faadc',
    'translated': '#FBEDA3',
    'blocked': '#FFB8B8',
    'update': '#FFE1AE',
  };
  
  return defaultColors[status.toLowerCase()] || '#000000';
}

/**
 * Get all available statuses for a workflow type
 */
export function getWorkflowStatuses(
  workflowType: 'objects' | 'templates' | 'documents' | 'dialogs' | 'loca',
  gameId?: string
): { status: string; color: string }[] {
  const statusConfigs = statusStore.getData();
  
  if (!statusConfigs || statusConfigs.length === 0) {
    return [];
  }

  const gameConfig = gameId 
    ? statusConfigs.find(config => config.game_id === gameId)
    : null;
  
  const companyConfig = statusConfigs.find(config => config.game_id === null);
  const config = gameConfig || companyConfig;
  
  if (!config) {
    return [];
  }

  const workflow: StatusWorkflow = config[workflowType] || {};
  
  return Object.entries(workflow).map(([status, { color }]) => ({
    status,
    color,
  }));
}

